// Profile mapping algorithm
export const determineProfile = (answers) => {
  const scores = {
    strategicArchitect: 0,
    dailyExecutor: 0,
    balancedHarmonizer: 0,
    projectJuggler: 0,
    intuitiveAdapter: 0,
    structuredMaximizer: 0
  };

  // Question 20: Natural planning horizon (STRONG INDICATOR)
  const q20 = answers[20];
  if (q20 === "I think in years - big picture goals" || q20 === "I think in quarters - 3-month chunks") {
    scores.strategicArchitect += 3;
  } else if (q20 === "I think in days - just today's priorities" || q20 === "I think in hours - time-blocking my day") {
    scores.dailyExecutor += 3;
  } else if (q20 === "I think in weeks - weekly rhythm") {
    scores.balancedHarmonizer += 2;
  } else if (q20 === "I think in months - what needs to happen this month") {
    scores.projectJuggler += 2;
  }

  // Question 19: Time frames needed (MULTIPLE SELECT)
  const q19 = answers[19] || [];
  if (q19.includes("Yearly goals and big picture") && q19.includes("Quarterly goals/projects")) {
    scores.strategicArchitect += 2;
  }
  if (q19.includes("Daily task lists") && q19.includes("Hourly time blocking")) {
    scores.dailyExecutor += 2;
  }
  if (q19.length >= 5) {
    scores.structuredMaximizer += 2;
  }

  // Question 23: Long-term projects (STRONG INDICATOR)
  const q23 = answers[23];
  if (q23 === "Always - I always have multiple projects going") {
    scores.projectJuggler += 3;
    scores.strategicArchitect += 1;
  } else if (q23 === "Often - I usually have 1-2 ongoing projects") {
    scores.projectJuggler += 2;
  } else if (q23 === "Never - I don't work on extended projects" || q23 === "Rarely - most of my work is day-to-day tasks") {
    scores.dailyExecutor += 1;
  }

  // Question 18: Schedule predictability
  const q18 = answers[18];
  if (q18 === "I know my whole year" || q18 === "I can predict 3-6 months out") {
    scores.strategicArchitect += 2;
  } else if (q18 === "I can only plan day-by-day") {
    scores.intuitiveAdapter += 2;
  }

  // Question 8: Detail level
  const q8 = answers[8];
  if (q8 === "Very detailed - I want every hour accounted for") {
    scores.dailyExecutor += 2;
    scores.structuredMaximizer += 2;
  } else if (q8 === "Minimal - just the absolute must-dos" || q8 === "Loose structure - just a list of what needs doing") {
    scores.intuitiveAdapter += 2;
  }

  // Question 7: Planning timing
  const q7 = answers[7];
  if (q7 === "I prefer NOT to pre-plan, I plan as things come" || q7 === "Whenever I remember to") {
    scores.intuitiveAdapter += 2;
  } else if (q7 === "Every morning for that day") {
    scores.dailyExecutor += 1;
  } else if (q7 === "Sunday evening for the week ahead") {
    scores.balancedHarmonizer += 1;
  }

  // Question 9 & 10: Roles (BALANCED HARMONIZER INDICATOR)
  const q9 = answers[9] || [];
  const q10 = answers[10];
  if (q9.length >= 5) {
    scores.balancedHarmonizer += 2;
  }
  if (q10 === "5-6 roles (I can juggle a lot)" || q10 === "7+ roles (I try to balance everything)") {
    scores.balancedHarmonizer += 2;
  }

  // Question 26: Life areas
  const q26 = answers[26];
  if (q26 === "5+ areas (I wear many hats)") {
    scores.balancedHarmonizer += 1;
  }

  // Question 16: Schedule structure
  const q16 = answers[16];
  if (q16 === "Very structured - same routine most days") {
    scores.structuredMaximizer += 2;
    scores.dailyExecutor += 1;
  } else if (q16 === "Flexible - every day is different" || q16 === "Chaotic - I never know what's coming") {
    scores.intuitiveAdapter += 2;
  }

  // Question 21: Project breakdown
  const q21 = answers[21];
  if (q21 === "Break into phases/milestones" || q21 === "Start with end goal, work backwards") {
    scores.strategicArchitect += 1;
    scores.projectJuggler += 1;
  } else if (q21 === "Wing it and figure it out as I go") {
    scores.intuitiveAdapter += 1;
  }

  // Question 3: Structure preference
  const q3 = answers[3];
  if (q3 === "I prefer printed templates I can just fill in") {
    scores.dailyExecutor += 1;
  } else if (q3 === "I just want the fastest way to get things done") {
    scores.intuitiveAdapter += 1;
  }

  // Question 6: What they hate (MULTIPLE SELECT)
  const q6 = answers[6] || [];
  if (q6.includes("Time blocking (too rigid)") || q6.includes("Detailed planning (prefer flexibility)")) {
    scores.intuitiveAdapter += 1;
  }

  // Question 5: What they enjoy (MULTIPLE SELECT)
  const q5 = answers[5] || [];
  if (q5.length >= 5) {
    scores.structuredMaximizer += 1;
  }
  if (q5.includes("Writing out my day hour by hour")) {
    scores.dailyExecutor += 1;
  }
  if (q5.includes("Monthly planning and goal-setting")) {
    scores.strategicArchitect += 1;
  }

  // Question 24: Task visibility
  const q24 = answers[24];
  if (q24 === "Motivated - I need to see everything to feel in control") {
    scores.structuredMaximizer += 1;
    scores.projectJuggler += 1;
  }

  // Question 25: Weekly planning priorities
  const q25 = answers[25];
  if (q25 === "Knowing exactly what time I'll do each task") {
    scores.dailyExecutor += 1;
  } else if (q25 === "Balancing different areas of life") {
    scores.balancedHarmonizer += 1;
  }

  // Question 17: Time controlled by others
  const q17 = answers[17];
  if (q17 === "50-75% (Others control most of my time)" || q17 === "75-100% (I'm constantly responding to others)") {
    scores.balancedHarmonizer += 1;
  }

  // Find the profile with the highest score
  const profileScores = Object.entries(scores);
  profileScores.sort((a, b) => b[1] - a[1]);

  // Return the profile with the highest score
  return profileScores[0][0];
};

// Profile names and descriptions
export const profiles = {
  strategicArchitect: {
    name: "The Strategic Architect",
    subtitle: "The Big-Picture Visionary",
    quickSummary: "You're a big-picture thinker who thinks in quarters and years, not days and weeks. Your brain naturally sees patterns, connections, and long-term trajectories."
  },
  dailyExecutor: {
    name: "The Daily Executor",
    subtitle: "The Detail-Oriented Doer",
    quickSummary: "You thrive on daily structure and detailed planning. You love checking off tasks and knowing exactly what each hour holds."
  },
  balancedHarmonizer: {
    name: "The Balanced Harmonizer",
    subtitle: "The Multi-Role Manager",
    quickSummary: "You juggle multiple life roles simultaneously and need visual systems to see how everything fits together throughout your week."
  },
  projectJuggler: {
    name: "The Project Juggler",
    subtitle: "The Multi-Project Manager",
    quickSummary: "You always have multiple projects going and need systems that help you track milestones, dependencies, and progress across all initiatives."
  },
  intuitiveAdapter: {
    name: "The Intuitive Adapter",
    subtitle: "The Flexible Planner",
    quickSummary: "You resist rigid systems and prefer fluid, adaptive planning that works with your ever-changing schedule and energy levels."
  },
  structuredMaximizer: {
    name: "The Structured Maximizer",
    subtitle: "The Efficiency Expert",
    quickSummary: "You want comprehensive systems that track everything from yearly goals to hourly time blocks, optimizing every aspect of your life."
  }
};
